/*
 * Sticky mobile add-to-cart bar.
 *
 * Drives the real product form rather than re-implementing cart logic: the bar's
 * button forwards clicks to the page's own submit button, so variant selection,
 * AJAX add and the cart drawer all keep working through product-form.js.
 */
if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        this.realSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.realQty = document.getElementById(`Quantity-${this.sectionId}`);
        this.realPrice = document.getElementById(`price-${this.sectionId}`);

        this.barPrice = this.querySelector('[data-sticky-price]');
        this.barQty = this.querySelector('[data-sticky-qty]');
        this.barSubmit = this.querySelector('[data-sticky-submit]');
        this.minus = this.querySelector('[data-sticky-qty-minus]');
        this.plus = this.querySelector('[data-sticky-qty-plus]');

        // Without a real button to forward to there is nothing to add to cart.
        if (!this.realSubmit) return;

        this.onSubmitClick = this.onSubmitClick.bind(this);
        this.onQtyChange = this.onQtyChange.bind(this);

        this.barSubmit.addEventListener('click', this.onSubmitClick);
        this.barQty.addEventListener('change', this.onQtyChange);
        this.minus.addEventListener('click', () => this.step(-1));
        this.plus.addEventListener('click', () => this.step(1));

        this.watchVisibility();
        this.watchPrice();
        this.watchSubmitState();
        this.syncPrice();
        this.syncSubmitState();
      }

      /*
       * Show the bar only once the real add-to-cart button has scrolled out of
       * view upwards. Checking `isIntersecting` alone would also fire on load,
       * when the button is simply still further down the page.
       */
      watchVisibility() {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrolledPast = entry.boundingClientRect.bottom <= 0;
              this.classList.toggle('is-visible', !entry.isIntersecting && scrolledPast);
            });
          },
          { threshold: 0 }
        );
        this.observer.observe(this.realSubmit);
      }

      /* Mirror the theme's own price markup so variant changes stay in sync. */
      watchPrice() {
        if (!this.realPrice || !this.barPrice) return;
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.realPrice, { childList: true, subtree: true, characterData: true });
      }

      /* Mirror disabled/sold-out state from the real button. */
      watchSubmitState() {
        this.stateObserver = new MutationObserver(() => this.syncSubmitState());
        this.stateObserver.observe(this.realSubmit, { attributes: true, attributeFilter: ['disabled'] });
      }

      /*
       * Pull out just the one price that applies. Copying the price block's HTML
       * wholesale brought the regular/sale/compare-at markup along with it and
       * rendered the price twice.
       */
      syncPrice() {
        if (!this.realPrice || !this.barPrice) return;

        const priceRoot = this.realPrice.querySelector('.price');
        const onSale = priceRoot && priceRoot.classList.contains('price--on-sale');
        const selector = onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular';
        const priceEl = this.realPrice.querySelector(selector) || this.realPrice.querySelector('.price-item');

        if (priceEl) this.barPrice.textContent = priceEl.textContent.trim();
      }

      syncSubmitState() {
        const disabled = this.realSubmit.hasAttribute('disabled');
        this.barSubmit.disabled = disabled;
        const label = this.barSubmit.querySelector('[data-sticky-submit-text]');
        if (label) {
          label.textContent = disabled
            ? this.dataset.soldOutText || 'Sold out'
            : this.dataset.addText || 'Add to cart';
        }
      }

      step(delta) {
        const current = parseInt(this.barQty.value, 10) || 1;
        const min = parseInt(this.barQty.min, 10) || 1;
        const next = Math.max(min, current + delta);
        this.barQty.value = next;
        this.onQtyChange();
      }

      onQtyChange() {
        const value = Math.max(parseInt(this.barQty.min, 10) || 1, parseInt(this.barQty.value, 10) || 1);
        this.barQty.value = value;
        this.minus.disabled = value <= (parseInt(this.barQty.min, 10) || 1);

        // Push the quantity into the real form so the add uses it.
        if (this.realQty) {
          this.realQty.value = value;
          this.realQty.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onSubmitClick(event) {
        event.preventDefault();
        if (this.barSubmit.disabled) return;
        this.onQtyChange();
        this.realSubmit.click();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.priceObserver?.disconnect();
        this.stateObserver?.disconnect();
      }
    }
  );
}
